// Dynamic Progress Bar Global Initialization
// This file should be loaded in theme.liquid before cart drawer functionality

// Custom Element Definition
if (!customElements.get('dynamic-progress-bar')) {
    class DynamicProgressBar extends HTMLElement {
        constructor() {
            super();
            this.cartCount = parseInt(this.dataset.cartCount) || 0;
            this.cartTotal = parseFloat(this.dataset.cartTotal) || 0;
            this.container = this.querySelector('.dynamic-progress-bar__container');
            this.headerText = this.querySelector('.dynamic-progress-bar__header h3');
            this.primaryTag = this.querySelector('.js-primary-tag');
            this.freeShippingTag = this.querySelector('.js-free-shipping-tag');
            this.savingTag = this.querySelector('.js-saving-tag');
            this.cartCountBadge = this.querySelector('.js-cart-count');
            this.progressBars = this.querySelectorAll('.dynamic-progress-bar__bar--progress--bar');
            this.badges = this.querySelectorAll('.dynamic-progress-bar__bar--badge');
            this.textSections = this.querySelectorAll('.js-text-section');
            this.footer = this.querySelector('.dynamic-progress-bar__footer');
            
            // Thresholds for progress sections
            this.thresholds = [5, 10, 20];
            this.discounts = [5, 10, 15];
            
            // Get free shipping threshold from liquid settings
            this.freeShippingThreshold = this.getFreeShippingThreshold();
            
            // Force fresh cart data fetch if in cart drawer or if initial data seems stale
            this.initialized = false;
            
            // Track animation state to prevent interference during animations
            this.isAnimating = false;
            
            this.init();
        }

        connectedCallback() {
            this.setupEventListeners();
            
            // Immediate initialization
            this.forceInitialize();
            
            // Multiple retry attempts for cart drawer
            setTimeout(() => this.forceInitialize(), 100);
            setTimeout(() => this.forceInitialize(), 300);
            setTimeout(() => this.forceInitialize(), 500);
            setTimeout(() => this.forceInitialize(), 1000);
        }
        
        forceInitialize() {
            this.handleCartUpdate();
            this.updateDisplay();
            this.initialized = true;
        }

        init() {
            this.updateDisplay();
            
            // Force a cart data refresh if we're in a cart drawer
            if (this.closest('[data-cart-drawer]') || this.closest('.cart-drawer')) {
                this.handleCartUpdate();
            }
        }

        getFreeShippingThreshold() {
            return parseFloat(this.dataset.freeShippingThreshold) || 0;
        }

        setupEventListeners() {
            // Listen for cart updates
            document.addEventListener('on:line-item:change', () => this.handleCartUpdate());
            document.addEventListener('custom:cart:updated', () => this.handleCartUpdate());
            document.addEventListener('on:cart-drawer:before-open', () => this.handleCartUpdate());
            document.addEventListener('cart-drawer:open', () => this.handleCartUpdate());
            document.addEventListener('cart:open', () => this.handleCartUpdate());
            
            // Listen for Shopify cart updates (fetch-based)
            document.addEventListener('cart:updated', (event) => {
                if (event.detail && event.detail.cart) {
                    this.cartCount = event.detail.cart.item_count;
                    this.cartTotal = event.detail.cart.total_price / 100; // Convert from cents
                    this.updateDisplay();
                }
            });
            
            // Set up intersection observer for cart drawer visibility
            this.setupVisibilityObserver();
        }
        
        setupVisibilityObserver() {
            if ('IntersectionObserver' in window) {
                this.observer = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting && entry.intersectionRatio > 0) {
                            // Element is visible, ensure it's properly initialized
                            this.handleCartUpdate();
                        }
                    });
                }, { threshold: 0.1 });
                
                this.observer.observe(this);
            }
        }
        
        disconnectedCallback() {
            if (this.observer) {
                this.observer.disconnect();
            }
        }

        handleCartUpdate() {
            fetch('/cart.js')
                .then(response => response.json())
                .then(cart => {
                    this.cartCount = cart.item_count;
                    this.cartTotal = cart.total_price / 100; // Convert from cents
                    this.updateDisplay();
                })
                .catch(error => {
                    console.error('Error fetching cart data:', error);
                    // Fallback: try to get data from dataset or global cart object
                    this.tryFallbackCartData();
                });
        }
        
        tryFallbackCartData() {            
            // Try to get from window.cart if available
            if (window.cart && window.cart.item_count !== undefined) {
                this.cartCount = window.cart.item_count;
                this.cartTotal = (window.cart.total_price || 0) / 100;
                this.updateDisplay();
                return;
            }
            
            // Try to get from Shopify cart object if available
            if (window.Shopify && window.Shopify.cart) {
                this.cartCount = window.Shopify.cart.item_count;
                this.cartTotal = (window.Shopify.cart.total_price || 0) / 100;
                this.updateDisplay();
                return;
            }
            
            // Final fallback: count items in DOM
            const cartItems = document.querySelectorAll('[data-cart-item], .cart-item, .line-item');
            if (cartItems.length > 0) {
                this.cartCount = cartItems.length;
                this.updateDisplay();
            }
        }

        updateDisplay() {
            this.updateCartCountBadge();
            this.updateProgressBars();
            this.updateBadges();
            this.updateTextSections();
            this.updateHeaderText();
            this.updateTags();
            this.updateFooter();
            
            // Check for milestone achievement (only trigger animation on exact milestones)
            if (this.cartCount === 5 || this.cartCount === 10 || this.cartCount === 20) {
                this.triggerAnimation();
            }
            
            // Manage animated state based on current cart count
            if (this.cartCount >= 20) {
                this.container.classList.add('animated');
            } else if (!this.isAnimating) {
                // Only remove animated state if we're not currently in an animation
                this.container.classList.remove('animated');
                // Also remove any newly-completed classes from sections
                this.textSections.forEach(section => {
                    section.classList.remove('newly-completed');
                });
            }
        }

        updateCartCountBadge() {
            if (this.cartCountBadge) {
                this.cartCountBadge.textContent = this.cartCount;
            }
        }

        updateProgressBars() {
            this.progressBars.forEach((bar, index) => {
                const threshold = this.thresholds[index];
                let progress = 0;
                
                if (this.cartCount >= threshold) {
                    progress = 100;
                    bar.className = 'dynamic-progress-bar__bar--progress--bar filled';
                } else if (index === 0 && this.cartCount > 0) {
                    // First section fills partially
                    progress = (this.cartCount / threshold) * 100;
                    bar.className = 'dynamic-progress-bar__bar--progress--bar filled';
                } else if (index === 1 && this.cartCount > 5) {
                    // Second section fills partially after first is complete
                    progress = ((this.cartCount - 5) / 5) * 100;
                    bar.className = 'dynamic-progress-bar__bar--progress--bar filled';
                } else if (index === 2 && this.cartCount > 10) {
                    // Third section fills partially after second is complete
                    progress = ((this.cartCount - 10) / 10) * 100;
                    bar.className = 'dynamic-progress-bar__bar--progress--bar filled';
                } else {
                    bar.className = 'dynamic-progress-bar__bar--progress--bar empty';
                }
                
                bar.style.setProperty('--filled-amount', progress);
            });
        }

        updateBadges() {
            this.badges.forEach((badge, index) => {
                const threshold = this.thresholds[index];
                if (this.cartCount >= threshold) {
                    badge.classList.add('active');
                } else {
                    badge.classList.remove('active');
                }
            });
        }

        updateTextSections() {
            // Skip text section updates in cart-drawer mode
            const isCartDrawerMode = this.container && this.container.classList.contains('dynamic-progress-bar__container--cart-drawer');
            if (!isCartDrawerMode) {
                this.textSections.forEach((section) => {
                    const threshold = parseInt(section.dataset.threshold);
                    if (this.cartCount >= threshold) {
                        section.classList.add('completed');
                    } else {
                        section.classList.remove('completed');
                    }
                });
            }
        }

        updateFooter() {
            const isCartDrawerMode = this.container && this.container.classList.contains('dynamic-progress-bar__container--cart-drawer');
            
            if (this.footer) {
                if (isCartDrawerMode) {
                    // Always hide footer in cart drawer mode (handled by CSS but ensure it's hidden)
                    this.footer.classList.add('hidden');
                    this.container.classList.add('footer-hidden');
                } else {
                    // Hide footer when free shipping tag is visible, show when hidden
                    const freeShippingVisible = this.freeShippingTag && this.freeShippingTag.style.display === 'flex';
                    
                    if (freeShippingVisible) {
                        this.footer.classList.add('hidden');
                        this.container.classList.add('footer-hidden');
                    } else {
                        this.footer.classList.remove('hidden');
                        this.container.classList.remove('footer-hidden');
                    }
                }
            }
        }

        updateHeaderText() {
            // Clear any existing content first
            this.headerText.innerHTML = '';
            
            if (this.cartCount === 0) {
                const formattedAmount = this.formatCurrency('34.99');
                this.headerText.innerHTML = this.dataset.textSaveUpTo || `Save up to <strong>${formattedAmount}</strong>`;
            } else if (this.cartCount >= 1 && this.cartCount < 5) {
                const itemsNeeded = 5 - this.cartCount;
                const template = this.dataset.textAddMoreFive || 'Add {{ count }} more for 5% off!';
                // Handle both regular and HTML-encoded placeholders
                let finalText = template.replace(/\{\{\s*count\s*\}\}/g, itemsNeeded);
                finalText = finalText.replace(/\{\s*count\s*\}/g, itemsNeeded);
                finalText = finalText.replace(/{{count}}/g, itemsNeeded);
                this.headerText.innerHTML = finalText;
            } else if (this.cartCount >= 5 && this.cartCount < 10) {
                const itemsNeeded = 10 - this.cartCount;
                const template = this.dataset.textAddMoreTen || 'Add {{ count }} more for 10% off!';
                // Handle both regular and HTML-encoded placeholders
                let finalText = template.replace(/\{\{\s*count\s*\}\}/g, itemsNeeded);
                finalText = finalText.replace(/\{\s*count\s*\}/g, itemsNeeded);
                finalText = finalText.replace(/{{count}}/g, itemsNeeded);
                this.headerText.innerHTML = finalText;
            } else if (this.cartCount >= 10 && this.cartCount < 20) {
                const itemsNeeded = 20 - this.cartCount;
                const template = this.dataset.textAddMoreFifteen || 'Add {{ count }} more for 15% off!';
                // Handle both regular and HTML-encoded placeholders
                let finalText = template.replace(/\{\{\s*count\s*\}\}/g, itemsNeeded);
                finalText = finalText.replace(/\{\s*count\s*\}/g, itemsNeeded);
                finalText = finalText.replace(/{{count}}/g, itemsNeeded);
                this.headerText.innerHTML = finalText;
            } else if (this.cartCount >= 20) {
                this.headerText.innerHTML = this.dataset.textDiscountFifteen || '<strong>15%</strong> off obtained!';
            }
        }

        updateTags() {
            // Calculate if free shipping threshold is met
            const cartTotalInCents = this.cartTotal * 100; // Convert to cents to match threshold
            const freeShippingMet = cartTotalInCents >= this.freeShippingThreshold && this.freeShippingThreshold > 0;
            const isCartDrawerMode = this.container && this.container.classList.contains('dynamic-progress-bar__container--cart-drawer');
            
            if (!isCartDrawerMode) {
                // Full mode tag updates
                if (this.cartCount === 0) {
                    // Only show "Buy More, Save More" when cart is empty
                    this.primaryTag.querySelector('span').textContent = this.dataset.textBuyMoreSaveMore;
                    this.primaryTag.style.display = 'flex';
                    this.freeShippingTag.style.display = 'none';
                    this.savingTag.style.display = 'none';
                } else if (this.cartCount >= 1 && this.cartCount < 5) {
                    this.primaryTag.querySelector('span').textContent = this.dataset.textSpecialRewards;
                    this.primaryTag.style.display = 'flex';
                    
                    // Show free shipping tag if threshold is met
                    if (freeShippingMet) {
                        this.freeShippingTag.style.display = 'flex';
                    } else {
                        this.freeShippingTag.style.display = 'none';
                    }
                    
                    this.savingTag.style.display = 'none';
                } else if (this.cartCount >= 5) {
                    // Calculate savings based on discount tier
                    let discountPercent = 0;
                    if (this.cartCount >= 20) discountPercent = 15;
                    else if (this.cartCount >= 10) discountPercent = 10;
                    else if (this.cartCount >= 5) discountPercent = 5;
                    
                    // Apply VAT rate to cart total before calculating savings (consistent with existing pricing logic)
                    const vatAdjustedTotal = this.cartTotal * (window.vat_rate || 1);
                    const savings = (vatAdjustedTotal * discountPercent / 100).toFixed(2);
                    const formattedSavings = this.formatCurrency(savings);
                    
                    // Hide primary tag for 5+ items - only show savings and free shipping
                    this.primaryTag.style.display = 'none';
                    
                    // Show free shipping tag if threshold is met
                    if (freeShippingMet) {
                        this.freeShippingTag.style.display = 'flex';
                    } else {
                        this.freeShippingTag.style.display = 'none';
                    }
                    
                    // Use translated saving text with amount replacement
                    const savingText = this.dataset.textSavingTemplate.replace('{{ amount }}', formattedSavings);
                    this.savingTag.querySelector('span').textContent = savingText;
                    this.savingTag.style.display = 'flex';
                }
            } else {
                // Cart drawer mode - simplified tag display (free shipping tags allowed)
                if (this.cartCount === 0) {
                    this.primaryTag.querySelector('span').textContent = this.dataset.textBuyMoreSaveMore;
                    this.primaryTag.style.display = 'flex';
                    this.freeShippingTag.style.display = 'none';
                    this.savingTag.style.display = 'none';
                } else if (this.cartCount >= 5) {
                    // Calculate savings based on discount tier
                    let discountPercent = 0;
                    if (this.cartCount >= 20) discountPercent = 15;
                    else if (this.cartCount >= 10) discountPercent = 10;
                    else if (this.cartCount >= 5) discountPercent = 5;
                    
                    // Apply VAT rate to cart total before calculating savings (consistent with existing pricing logic)
                    const vatAdjustedTotal = this.cartTotal * (window.vat_rate || 1);
                    const savings = (vatAdjustedTotal * discountPercent / 100).toFixed(2);
                    const formattedSavings = this.formatCurrency(savings);
                    
                    // Hide primary tag for 5+ items - show savings and free shipping if qualified
                    this.primaryTag.style.display = 'none';
                    
                    // Show free shipping tag if threshold is met
                    if (freeShippingMet) {
                        this.freeShippingTag.style.display = 'flex';
                    } else {
                        this.freeShippingTag.style.display = 'none';
                    }
                    
                    // Use translated saving text with amount replacement
                    const savingText = this.dataset.textSavingTemplate.replace('{{ amount }}', formattedSavings);
                    this.savingTag.querySelector('span').textContent = savingText;
                    this.savingTag.style.display = 'flex';
                } else {
                    // For 1-4 items in cart drawer, show simplified "Special Rewards" + free shipping if qualified
                    this.primaryTag.querySelector('span').textContent = this.dataset.textSpecialRewards;
                    this.primaryTag.style.display = 'flex';
                    
                    // Show free shipping tag if threshold is met
                    if (freeShippingMet) {
                        this.freeShippingTag.style.display = 'flex';
                    } else {
                        this.freeShippingTag.style.display = 'none';
                    }
                    
                    this.savingTag.style.display = 'none';
                }
            }
        }

        triggerAnimation() {
            // Store the current header text to restore later
            const originalText = this.headerText.innerHTML;
            
            // Find the specific section that just reached its milestone
            const targetSection = this.querySelector(`[data-threshold="${this.cartCount}"]`);
            
            // Show celebration message based on milestone
            let celebrationText = '';
            if (this.cartCount === 5) {
                celebrationText = this.dataset.textDiscountFive;
            } else if (this.cartCount === 10) {
                celebrationText = this.dataset.textDiscountTen;
            } else if (this.cartCount === 20) {
                celebrationText = this.dataset.textDiscountFifteen;
            }
            
            // Set animation flag to prevent updateDisplay from interfering
            this.isAnimating = true;
            
            // Apply animation styles and celebration text
            this.container.classList.add('animated');
            this.headerText.innerHTML = celebrationText;
            
            // Add newly-completed class to the specific section that triggered this milestone
            if (targetSection) {
                targetSection.classList.add('newly-completed');
            }
            
            // Set timeout to clear animation and restore normal state
            setTimeout(() => {
                // Clear animation flag
                this.isAnimating = false;
                
                // Only clear animation if cart count is below 20
                if (this.cartCount < 20) {
                    this.container.classList.remove('animated');
                }
                // Remove newly-completed class from all sections
                this.textSections.forEach(section => {
                    section.classList.remove('newly-completed');
                });
                // Restore normal text by calling updateHeaderText
                this.updateHeaderText();
            }, 5000);
        }

        formatCurrency(amount) {
            return DynamicProgressBar.formatCurrencyStatic(amount, this.dataset.currency);
        }

        static formatCurrencyStatic(amount, currency) {
            // Use the existing formatMoney function with exchange rate conversion
            const exchangeRate = window.getExchangeRate ? window.getExchangeRate(currency) : 1;
            const format = window.money_format || '{{ amount }}';
            
            // Convert amount to cents (formatMoney expects cents)
            const amountInCents = parseFloat(amount) * 100;
            
            return window.formatMoney ? 
                window.formatMoney(amountInCents, format, exchangeRate) : 
                `${currency} ${amount}`;
        }
    }

    customElements.define('dynamic-progress-bar', DynamicProgressBar);
}

// Expose a global method to manually initialize all progress bars
window.initializeDynamicProgressBars = function() {
    const progressBars = document.querySelectorAll('dynamic-progress-bar');
    
    progressBars.forEach((bar, index) => {
        if (bar.handleCartUpdate) {
            bar.handleCartUpdate();
        } else {
            // Manually create the methods if custom element didn't initialize
            window.manuallyInitializeProgressBar(bar);
        }
    });
};

// Manual initialization function that doesn't rely on custom element
window.manuallyInitializeProgressBar = function(element) {
    
    // Get cart data
    fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
            const cartCount = cart.item_count;
            const cartTotal = cart.total_price / 100;
            
            // Update cart count badge
            const badge = element.querySelector('.js-cart-count');
            if (badge) {
                badge.textContent = cartCount;
            }
            
            // Update header text
            const headerText = element.querySelector('.dynamic-progress-bar__header h3');
            if (headerText) {
                if (cartCount === 0) {
                    const formattedAmount = DynamicProgressBar.formatCurrencyStatic('34.99', element.dataset.currency);
                    const saveUpToText = element.dataset.textSaveUpTo || `Save up to <strong>${formattedAmount}</strong>`;
                    headerText.innerHTML = saveUpToText;
                } else if (cartCount >= 1 && cartCount < 5) {
                    const needed = 5 - cartCount;
                    const addMoreFiveText = element.dataset.textAddMoreFive || 'Add {{ count }} more for 5% off!';
                    headerText.innerHTML = addMoreFiveText.replace('{{ count }}', needed);
                } else if (cartCount >= 5 && cartCount < 10) {
                    const needed = 10 - cartCount;
                    const addMoreTenText = element.dataset.textAddMoreTen || 'Add {{ count }} more for 10% off!';
                    headerText.innerHTML = addMoreTenText.replace('{{ count }}', needed);
                } else if (cartCount >= 10 && cartCount < 20) {
                    const needed = 20 - cartCount;
                    const addMoreFifteenText = element.dataset.textAddMoreFifteen || 'Add {{ count }} more for 15% off!';
                    headerText.innerHTML = addMoreFifteenText.replace('{{ count }}', needed);
                } else if (cartCount >= 20) {
                    const discountFifteenText = element.dataset.textDiscountFifteen || '<strong>15%</strong> off obtained!';
                    headerText.innerHTML = discountFifteenText;
                }
            }
            
            // Update progress bars
            const progressBars = element.querySelectorAll('.dynamic-progress-bar__bar--progress--bar');
            const thresholds = [5, 10, 20];
            
            progressBars.forEach((bar, index) => {
                const threshold = thresholds[index];
                let progress = 0;
                
                if (cartCount >= threshold) {
                    progress = 100;
                    bar.className = 'dynamic-progress-bar__bar--progress--bar filled';
                } else if (index === 0 && cartCount > 0) {
                    progress = (cartCount / threshold) * 100;
                    bar.className = 'dynamic-progress-bar__bar--progress--bar filled';
                } else if (index === 1 && cartCount > 5) {
                    progress = ((cartCount - 5) / 5) * 100;
                    bar.className = 'dynamic-progress-bar__bar--progress--bar filled';
                } else if (index === 2 && cartCount > 10) {
                    progress = ((cartCount - 10) / 10) * 100;
                    bar.className = 'dynamic-progress-bar__bar--progress--bar filled';
                } else {
                    bar.className = 'dynamic-progress-bar__bar--progress--bar empty';
                }
                
                bar.style.setProperty('--filled-amount', progress);
            });
            
            // Update badges
            const badges = element.querySelectorAll('.dynamic-progress-bar__bar--badge');
            badges.forEach((badge, index) => {
                const threshold = thresholds[index];
                if (cartCount >= threshold) {
                    badge.classList.add('active');
                } else {
                    badge.classList.remove('active');
                }
            });
            
            // Update text sections (only if not in cart-drawer mode)
            const isCartDrawerMode = element.querySelector('.dynamic-progress-bar__container--cart-drawer');
            if (!isCartDrawerMode) {
                const textSections = element.querySelectorAll('.js-text-section');
                textSections.forEach((section) => {
                    const threshold = parseInt(section.dataset.threshold);
                    if (cartCount >= threshold) {
                        section.classList.add('completed');
                    } else {
                        section.classList.remove('completed');
                    }
                });
            }
            
            // Update footer visibility
            const footer = element.querySelector('.dynamic-progress-bar__footer');
            const container = element.querySelector('.dynamic-progress-bar__container');
            if (footer && container) {
                const isCartDrawerMode = element.querySelector('.dynamic-progress-bar__container--cart-drawer');
                if (isCartDrawerMode) {
                    footer.classList.add('hidden');
                    container.classList.add('footer-hidden');
                } else {
                    const freeShippingVisible = freeShippingTag && freeShippingTag.style.display === 'flex';
                    if (freeShippingVisible) {
                        footer.classList.add('hidden');
                        container.classList.add('footer-hidden');
                    } else {
                        footer.classList.remove('hidden');
                        container.classList.remove('footer-hidden');
                    }
                }
            }

            // Update tags (simplified for cart-drawer mode)
            const primaryTag = element.querySelector('.js-primary-tag');
            const freeShippingTag = element.querySelector('.js-free-shipping-tag');
            const savingTag = element.querySelector('.js-saving-tag');
            
            if (!isCartDrawerMode) {
                // Full mode tag updates
                if (cartCount === 0) {
                    if (primaryTag) {
                        const buyMoreSaveMoreText = element.dataset.textBuyMoreSaveMore || 'Buy More, Save More';
                        primaryTag.querySelector('span').textContent = buyMoreSaveMoreText;
                        primaryTag.style.display = 'flex';
                    }
                    if (freeShippingTag) freeShippingTag.style.display = 'none';
                    if (savingTag) savingTag.style.display = 'none';
                } else if (cartCount >= 1 && cartCount < 5) {
                    if (primaryTag) {
                        const specialRewardsText = element.dataset.textSpecialRewards || 'Special Rewards';
                        primaryTag.querySelector('span').textContent = specialRewardsText;
                        primaryTag.style.display = 'flex';
                    }
                    if (freeShippingTag) freeShippingTag.style.display = 'none';
                    if (savingTag) savingTag.style.display = 'none';
                } else if (cartCount >= 5) {
                    let discountPercent = 0;
                    if (cartCount >= 20) discountPercent = 15;
                    else if (cartCount >= 10) discountPercent = 10;
                    else if (cartCount >= 5) discountPercent = 5;
                    
                    // Apply VAT rate to cart total before calculating savings (consistent with existing pricing logic)
                    const vatAdjustedTotal = cartTotal * (window.vat_rate || 1);
                    const savings = (vatAdjustedTotal * discountPercent / 100).toFixed(2);
                    const formattedSavings = DynamicProgressBar.formatCurrencyStatic(savings, element.dataset.currency);
                    
                    if (primaryTag) primaryTag.style.display = 'none';
                    if (freeShippingTag) freeShippingTag.style.display = 'none';
                    if (savingTag) {
                        const savingTemplate = element.dataset.textSavingTemplate || 'Saving {{ amount }}';
                        const savingText = savingTemplate.replace('{{ amount }}', formattedSavings);
                        savingTag.querySelector('span').textContent = savingText;
                        savingTag.style.display = 'flex';
                    }
                }
            } else {
                // Cart drawer mode - simplified tag display (free shipping tags allowed)
                // Calculate if free shipping threshold is met for cart drawer mode
                const cartTotalInCents = cartTotal * 100;
                const freeShippingThreshold = parseFloat(element.dataset.freeShippingThreshold) || 0;
                const freeShippingMet = cartTotalInCents >= freeShippingThreshold && freeShippingThreshold > 0;
                
                if (cartCount === 0) {
                    if (primaryTag) {
                        const buyMoreSaveMoreText = element.dataset.textBuyMoreSaveMore || 'Buy More, Save More';
                        primaryTag.querySelector('span').textContent = buyMoreSaveMoreText;
                        primaryTag.style.display = 'flex';
                    }
                    if (freeShippingTag) freeShippingTag.style.display = 'none';
                    if (savingTag) savingTag.style.display = 'none';
                } else if (cartCount >= 5) {
                    let discountPercent = 0;
                    if (cartCount >= 20) discountPercent = 15;
                    else if (cartCount >= 10) discountPercent = 10;
                    else if (cartCount >= 5) discountPercent = 5;
                    
                    // Apply VAT rate to cart total before calculating savings (consistent with existing pricing logic)
                    const vatAdjustedTotal = cartTotal * (window.vat_rate || 1);
                    const savings = (vatAdjustedTotal * discountPercent / 100).toFixed(2);
                    const formattedSavings = DynamicProgressBar.formatCurrencyStatic(savings, element.dataset.currency);
                    
                    if (primaryTag) primaryTag.style.display = 'none';
                    
                    // Show free shipping tag if threshold is met
                    if (freeShippingTag) {
                        if (freeShippingMet) {
                            freeShippingTag.style.display = 'flex';
                        } else {
                            freeShippingTag.style.display = 'none';
                        }
                    }
                    
                    if (savingTag) {
                        const savingTemplate = element.dataset.textSavingTemplate || 'Saving {{ amount }}';
                        const savingText = savingTemplate.replace('{{ amount }}', formattedSavings);
                        savingTag.querySelector('span').textContent = savingText;
                        savingTag.style.display = 'flex';
                    }
                } else {
                    // For 1-4 items in cart drawer, show simplified "Special Rewards" + free shipping if qualified
                    if (primaryTag) {
                        const specialRewardsText = element.dataset.textSpecialRewards || 'Special Rewards';
                        primaryTag.querySelector('span').textContent = specialRewardsText;
                        primaryTag.style.display = 'flex';
                    }
                    
                    // Show free shipping tag if threshold is met
                    if (freeShippingTag) {
                        if (freeShippingMet) {
                            freeShippingTag.style.display = 'flex';
                        } else {
                            freeShippingTag.style.display = 'none';
                        }
                    }
                    
                    if (savingTag) savingTag.style.display = 'none';
                }
            }
            
            // Manage animated state based on cart count (consistent with custom element)
            if (cartCount >= 20) {
                container.classList.add('animated');
            } else {
                // Remove animated state if cart count is below 20
                container.classList.remove('animated');
                // Also remove any newly-completed classes from sections
                const textSections = element.querySelectorAll('.js-text-section');
                textSections.forEach(section => {
                    section.classList.remove('newly-completed');
                });
            }
            
        })
        .catch(error => {
            console.error('Error in manual initialization:', error);
        });
};

// Set up MutationObserver to detect when progress bars are added to DOM
function setupMutationObserver() {
    if ('MutationObserver' in window) {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) { // Element node
                        // Check if the added node is a progress bar
                        if (node.tagName === 'DYNAMIC-PROGRESS-BAR') {
                            setTimeout(() => {
                                window.manuallyInitializeProgressBar(node);
                            }, 200);
                        }
                        // Check if any child nodes are progress bars
                        const progressBars = node.querySelectorAll ? node.querySelectorAll('dynamic-progress-bar') : [];
                        if (progressBars.length > 0) {
                            progressBars.forEach(bar => {
                                setTimeout(() => {
                                    window.manuallyInitializeProgressBar(bar);
                                }, 200);
                            });
                        }
                    }
                });
            });
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
        
    }
}

// Auto-initialize when cart drawer opens (common event names)
document.addEventListener('DOMContentLoaded', () => {
    
    setupMutationObserver();
    
    const cartDrawerEvents = [
        'cart-drawer:open',
        'cart:open', 
        'drawer:open',
        'cart-drawer:loaded',
        'cart:loaded',
        'cart:refresh',
        'cart:update'
    ];
    
    cartDrawerEvents.forEach(eventName => {
        document.addEventListener(eventName, () => {
            setTimeout(() => {
                window.initializeDynamicProgressBars();
            }, 200);
        });
    });
    
    // Also listen for any clicks that might open cart drawer
    document.addEventListener('click', (e) => {
        const target = e.target.closest('[data-cart-drawer-trigger], [href="#cart-drawer"], .cart-trigger, .cart-icon, [data-cart-open]');
        if (target) {
            setTimeout(() => {
                window.initializeDynamicProgressBars();
            }, 500);
            setTimeout(() => {
                window.initializeDynamicProgressBars();
            }, 1000);
            setTimeout(() => {
                window.initializeDynamicProgressBars();
            }, 1500);
        }
    });
    
    // Also try to initialize whenever cart drawer becomes visible
    const checkForCartDrawer = () => {
        const progressBars = document.querySelectorAll('dynamic-progress-bar');
        progressBars.forEach(bar => {
            if (bar.offsetParent !== null) { // Element is visible
                const badge = bar.querySelector('.js-cart-count');
                if (badge && badge.textContent === '0') {   
                    window.manuallyInitializeProgressBar(bar);
                }
            }
        });
    };
    
    // Check every 3 seconds for visible but uninitialized progress bars
    setInterval(checkForCartDrawer, 3000);
});

// Fallback: periodically check for uninitialized progress bars
setInterval(() => {
    const progressBars = document.querySelectorAll('dynamic-progress-bar');
    progressBars.forEach(bar => {
        // Check if the cart count badge shows 0 but we actually have items
        const badge = bar.querySelector('.js-cart-count');
        if (badge && badge.textContent === '0') {
            // Try to reinitialize
            window.manuallyInitializeProgressBar(bar);
        }
    });
}, 5000); 